import {
  NOTIFICATION_CHANNELS,
  type INotificationChannelAvailability,
  type INotificationSettingRecord,
  type TNotificationChannel,
  type TNotificationPreferenceProperties,
  type TOfferedChannelsByEvent,
} from './types';

/** Server-side event names are namespaced; the prefix adds nothing to a property key. */
const EVENT_NAME_PREFIXES = ['OFFICE_EVENT_', 'OFFICE_SETTING_'];

const PREFERENCE_KEY_PREFIX = 'notif_pref_';

/** Value used when an office has switched off every channel it can control. */
export const NO_CHANNELS_ENABLED = 'none';

const AVAILABILITY_FLAG: Record<TNotificationChannel, keyof INotificationChannelAvailability> = {
  email: 'isEmail',
  push: 'isPush',
  text: 'isText',
};

/**
 * Derives which channels each notification type genuinely offers, from the
 * `isEmail` / `isPush` / `isText` flags on `notificationTypeMap`.
 *
 * This must be the source of truth for availability: the API returns all three
 * channel booleans as `true` even for notifications that only support one, so
 * reading availability off the settings record overstates what the office
 * actually configured.
 */
export function toOfferedChannelsByEvent(
  notificationTypeMap: Record<string, INotificationChannelAvailability | undefined>,
): TOfferedChannelsByEvent {
  const offered: TOfferedChannelsByEvent = {};

  Object.entries(notificationTypeMap).forEach(([event, config]) => {
    offered[event] = NOTIFICATION_CHANNELS.filter(
      (channel) => config?.[AVAILABILITY_FLAG[channel]] === true,
    );
  });

  return offered;
}

/**
 * Channel keys the API defined on this record. Only a fallback for event types
 * missing from the type map — see `toOfferedChannelsByEvent` for why key
 * presence is not a reliable availability signal.
 */
export function getDefinedChannels(setting: INotificationSettingRecord): TNotificationChannel[] {
  return NOTIFICATION_CHANNELS.filter((channel) => setting[channel] !== undefined);
}

/**
 * Maps a server event name to its property key, e.g.
 * `OFFICE_EVENT_OFFER_RECEIVED` -> `notif_pref_offer_received`.
 */
export function toNotificationPreferenceKey(event: string): string {
  const prefix = EVENT_NAME_PREFIXES.find((candidate) => event.startsWith(candidate));
  const name = prefix ? event.slice(prefix.length) : event;

  return `${PREFERENCE_KEY_PREFIX}${name.toLowerCase()}`;
}

/**
 * Describes how an office has configured its notifications: one property per
 * notification type listing the channels it left switched on, in a stable order.
 * Channels the notification does not offer are excluded entirely. Pure.
 */
export function buildNotificationPreferenceProperties(
  settings: INotificationSettingRecord[],
  offeredChannelsByEvent: TOfferedChannelsByEvent = {},
): TNotificationPreferenceProperties {
  const properties: TNotificationPreferenceProperties = {};

  settings.forEach((setting) => {
    const offered = offeredChannelsByEvent[setting.event] ?? getDefinedChannels(setting);
    const enabled = offered.filter((channel) => setting[channel] === true);

    properties[toNotificationPreferenceKey(setting.event)] = enabled.length
      ? enabled.join(',')
      : NO_CHANNELS_ENABLED;
  });

  return properties;
}
